<template>
  <div v-if="safeCitations.length" class="source-disclosure">
    <Tooltip :text="citationsCountLabel" :disabled="sourcesOpen">
      <button
        ref="sourceBadge"
        type="button"
        class="source-badge"
        :aria-controls="sourcePopoverId"
        :aria-expanded="String(sourcesOpen)"
        :aria-label="sourceButtonLabel"
        aria-haspopup="true"
        @click="toggleSources"
      >
        <icon-document-search class="source-badge-icon" />
      </button>
    </Tooltip>

    <div
      v-if="sourcesOpen"
      :id="sourcePopoverId"
      class="source-popover"
      :class="{
        'source-popover--below': sourcePopoverPlacement === 'bottom',
        'source-popover--ready': sourcePopoverReady
      }"
      ref="sourcePopover"
      :style="sourcePopoverStyle"
    >
      <div class="source-popover__heading">
        <strong>{{ citationsCountLabel }}</strong>
      </div>
      <ul class="source-list">
        <li v-for="(citation, index) in safeCitations" :key="`${citation.source}-${index}`">
          <a
            ref="sourceLinks"
            class="source-list__link"
            :href="citation.source"
            target="_blank"
            rel="noopener noreferrer"
          >
            <icon-external-link class="source-list__icon" :size="14" />
            <strong>{{ citation.title }}</strong>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import IconDocumentSearch from './icons/icons/IconDocumentSearch.vue'
import IconExternalLink from './icons/icons/IconExternalLink.vue'
import Tooltip from './Tooltip.vue'
import { selectPluralForm } from '@/util/pluralize'
import { safeHttpUrl } from '@/util/url'

export default {
  name: 'CitationsBadge',
  components: {
    IconDocumentSearch,
    IconExternalLink,
    Tooltip
  },
  props: {
    citations: {
      type: Array,
      required: true
    },
    colors: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      sourcesOpen: false,
      sourcePopoverReady: false,
      sourcePopoverPlacement: 'top',
      sourcePopoverTop: '0px',
      sourcePopoverLeft: '0px',
      sourcePopoverMaxHeight: null,
      sourcePopoverNaturalHeight: 0
    }
  },
  beforeDestroy () {
    this.removeSourceListeners()
  },
  watch: {
    citations () {
      this.closeSources()
    }
  },
  computed: {
    safeCitations () {
      return this.citations.reduce((safe, citation) => {
        const source = safeHttpUrl(citation && citation.source)
        if (!source) return safe
        safe.push({ ...citation, source })
        return safe
      }, [])
    },
    citationNoun () {
      return selectPluralForm(
        this.$t('interface.citations'),
        this.safeCitations.length,
        this.$i18n && this.$i18n.locale
      )
    },
    sourceButtonLabel () {
      return `Show ${this.safeCitations.length} ${this.citationNoun}`
    },
    citationsCountLabel () {
      return `${this.safeCitations.length} ${this.citationNoun}`
    },
    sourcePopoverId () {
      return `message-sources-${this._uid}`
    },
    sourcePopoverStyle () {
      const style = {
        top: this.sourcePopoverTop,
        left: this.sourcePopoverLeft
      }
      if (this.sourcePopoverMaxHeight) {
        style.maxHeight = this.sourcePopoverMaxHeight
      }
      return style
    }
  },
  methods: {
    toggleSources () {
      if (this.sourcesOpen) {
        this.closeSources()
        return
      }

      this.sourcesOpen = true
      document.addEventListener('click', this.handleSourceDocumentClick)
      document.addEventListener('keydown', this.handleSourceKeydown)
      window.addEventListener('resize', this.positionSourcePopover)
      window.addEventListener('scroll', this.positionSourcePopover, true)
      this.$nextTick(() => {
        this.positionSourcePopover()
        const firstLink = this.$refs.sourceLinks && this.$refs.sourceLinks[0]
        if (firstLink) firstLink.focus({ preventScroll: true })
      })
    },
    closeSources (returnFocus = false) {
      if (!this.sourcesOpen) return
      this.sourcesOpen = false
      this.sourcePopoverReady = false
      this.sourcePopoverPlacement = 'top'
      this.sourcePopoverTop = '0px'
      this.sourcePopoverLeft = '0px'
      this.sourcePopoverMaxHeight = null
      this.sourcePopoverNaturalHeight = 0
      this.removeSourceListeners()
      if (returnFocus) {
        this.$nextTick(() => this.$refs.sourceBadge && this.$refs.sourceBadge.focus())
      }
    },
    handleSourceDocumentClick (event) {
      if (!this.$el.contains(event.target)) this.closeSources()
    },
    handleSourceKeydown (event) {
      if (event.key === 'Escape') this.closeSources(true)
    },
    sourcePopoverHorizontalOffset (badgeLeft, popoverWidth, areaRight, gutter = 8) {
      return Math.min(0, areaRight - gutter - badgeLeft - popoverWidth)
    },
    sourcePopoverClipRect (el) {
      let parent = el.parentElement
      while (parent) {
        const { overflow, overflowY } = window.getComputedStyle(parent)
        if (/(auto|scroll|hidden)/.test(overflowY) || /(auto|scroll|hidden)/.test(overflow)) {
          return parent.getBoundingClientRect()
        }
        parent = parent.parentElement
      }

      return {
        top: 0,
        bottom: window.innerHeight,
        left: 0,
        right: window.innerWidth
      }
    },
    sourcePopoverLayout ({
      badgeTop,
      badgeBottom,
      badgeLeft,
      popoverWidth,
      popoverHeight,
      areaTop,
      areaBottom,
      areaRight,
      gap = 6,
      gutter = 8
    }) {
      const spaceAbove = badgeTop - areaTop
      const spaceBelow = areaBottom - badgeBottom
      const placement = spaceAbove >= popoverHeight + gap || spaceAbove >= spaceBelow
        ? 'top'
        : 'bottom'
      const available = (placement === 'top' ? spaceAbove : spaceBelow) - gap
      const maxHeight = Math.max(0, available - gutter)
      const height = Math.min(popoverHeight, maxHeight)
      const top = placement === 'top'
        ? badgeTop - gap - height
        : badgeBottom + gap
      const left = badgeLeft + this.sourcePopoverHorizontalOffset(
        badgeLeft,
        popoverWidth,
        areaRight,
        gutter
      )

      return { top, left, placement, maxHeight }
    },
    positionSourcePopover () {
      if (!this.sourcesOpen || !this.$refs.sourcePopover) return

      const popover = this.$refs.sourcePopover
      const badgeRect = this.$el.getBoundingClientRect()
      const clipRect = this.sourcePopoverClipRect(this.$el)

      if (badgeRect.bottom < clipRect.top || badgeRect.top > clipRect.bottom) {
        this.closeSources()
        return
      }

      const areaTop = Math.max(0, clipRect.top)
      const areaBottom = Math.min(window.innerHeight, clipRect.bottom)
      const areaRight = Math.min(window.innerWidth, clipRect.right)
      const naturalHeight = this.sourcePopoverNaturalHeight || popover.offsetHeight
      if (!this.sourcePopoverNaturalHeight) {
        this.sourcePopoverNaturalHeight = naturalHeight
      }

      const layout = this.sourcePopoverLayout({
        badgeTop: badgeRect.top,
        badgeBottom: badgeRect.bottom,
        badgeLeft: badgeRect.left,
        popoverWidth: popover.offsetWidth,
        popoverHeight: naturalHeight,
        areaTop,
        areaBottom,
        areaRight
      })

      this.sourcePopoverPlacement = layout.placement
      this.sourcePopoverTop = `${layout.top}px`
      this.sourcePopoverLeft = `${layout.left}px`
      this.sourcePopoverMaxHeight = `${layout.maxHeight}px`
      this.sourcePopoverReady = true
    },
    removeSourceListeners () {
      document.removeEventListener('click', this.handleSourceDocumentClick)
      document.removeEventListener('keydown', this.handleSourceKeydown)
      window.removeEventListener('resize', this.positionSourcePopover)
      window.removeEventListener('scroll', this.positionSourcePopover, true)
    }
  }
}
</script>
